import React, { useState, useEffect } from 'react';
import { Modal, Button, Row, Col, Card } from 'react-bootstrap';
import { FiCheckSquare, FiSquare } from 'react-icons/fi';
import { exportToExcel, exportToCSV } from '../../utils/reportUtils';
import { toast } from 'react-toastify';

const ReportDownloader = ({ 
  show, 
  handleClose, 
  data, 
  availableColumns, 
  title = "Export Report",
  fileName = "report"
}) => {
  const [selectedColumns, setSelectedColumns] = useState([]);

  useEffect(() => {
    // By default, select all columns
    if (availableColumns && availableColumns.length > 0) {
      setSelectedColumns(availableColumns.map(col => col.key));
    }
  }, [availableColumns]);

  const toggleColumn = (key) => {
    if (selectedColumns.includes(key)) {
      setSelectedColumns(selectedColumns.filter(c => c !== key));
    } else {
      setSelectedColumns([...selectedColumns, key]);
    }
  };

  const selectAll = () => {
    setSelectedColumns(availableColumns.map(col => col.key));
  };

  const deselectAll = () => {
    setSelectedColumns([]);
  };

  const handleExport = (format) => {
    if (selectedColumns.length === 0) {
      toast.warning("Please select at least one field to export");
      return;
    }

    if (!data || data.length === 0) {
      toast.warning("No data available to export");
      return;
    }

    const exportColumns = availableColumns.filter(col => selectedColumns.includes(col.key));

    try {
      if (format === 'excel') {
        exportToExcel(data, exportColumns, fileName);
      } else if (format === 'csv') {
        exportToCSV(data, exportColumns, fileName);
      } else if (format === 'pdf') {
        // Fallback or implementation for PDF
        toast.info("Excel/CSV export completed. PDF customization coming soon!");
        // For now we'll just do Excel
        exportToExcel(data, exportColumns, fileName);
      }
      toast.success("Report downloaded successfully");
      handleClose();
    } catch (error) {
      console.error("Export error", error);
      toast.error("Failed to export report");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Choose Fields to Include</h6>
          <div className="btn-group btn-group-sm">
            <Button variant="outline-secondary" onClick={selectAll}>Select All</Button>
            <Button variant="outline-secondary" onClick={deselectAll}>Deselect All</Button>
          </div>
        </div>

        <Card className="mb-3">
          <Card.Body style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Row>
              {availableColumns.map((col) => (
                <Col md={6} key={col.key} className="mb-2">
                  <div 
                    className={`p-2 border rounded cursor-pointer d-flex align-items-center ${selectedColumns.includes(col.key) ? 'bg-light-primary border-primary' : 'bg-white'}`}
                    onClick={() => toggleColumn(col.key)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="me-2 fs-8">
                      {selectedColumns.includes(col.key) ? (
                        <FiCheckSquare className="text-primary" />
                      ) : (
                        <FiSquare className="text-muted" />
                      )}
                    </div>
                    <span className="fs-9">{col.header}</span>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>

        <p className="small text-muted mb-0">
          Total Rows to export: <strong>{data?.length || 0}</strong>
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <div className="d-flex gap-2">
          <Button variant="outline-success" onClick={() => handleExport('csv')}>
            Download CSV
          </Button>
          <Button variant="primary" onClick={() => handleExport('excel')}>
            Download Excel
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportDownloader;
